import { LoadingButton } from '@mui/lab';
import { Container, Link, Skeleton, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { QRCodeCanvas } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { CopyTextField, LogoBack, OneTimeCodeTextField } from 'components';
import { AUTH_PATHS } from 'constants/paths';
import { useIsMountedRef } from 'hooks';
import { FormProvider } from 'providers';
import { useAuthSelector } from 'stores';

const MfaSetup = () => {
  // hooks
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const { handleSetupTOTPAuth, handleCompleteTOTPAuthSetup } =
    useAuthSelector();

  const { state } = useLocation();

  // form
  const methods = useForm({
    defaultValues: {
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: '',
    },
  });

  // state
  const [secretCode, setSecretCode] = useState('');

  const {
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { isSubmitting },
  } = methods;

  const isValidForm =
    watch('code1') &&
    watch('code2') &&
    watch('code3') &&
    watch('code4') &&
    watch('code5') &&
    watch('code6');

  const issuerPrefix =
    process.env.REACT_APP_ENVIRONMENT === 'main'
      ? ''
      : `[${process.env.REACT_APP_ENVIRONMENT}] `.toUpperCase();
  const issuer = encodeURIComponent(`${issuerPrefix} INBEE`);
  const qrCode = `otpauth://totp/${issuer}:${state?.email}?secret=${secretCode}&issuer=${issuer}`;

  const init = async () => {
    try {
      const code = await handleSetupTOTPAuth();
      //console.log('Returned code:', code);
      setSecretCode(code);
    } catch (error) {
      enqueueSnackbar(
        error.message || 'Errore durante la generazione del codice MFA',
        { variant: 'error' }
      );
      console.error('init error', error);
    }
  };

  useEffect(() => {
    init();
  }, []);

  // functions
  const onSubmit = async data => {
    try {
      const code = Object.entries(data)
        .filter(([key]) => key.startsWith('code'))
        .map(([, value]) => value)
        .join('');

      await handleCompleteTOTPAuthSetup(code);
    } catch (error) {
      console.error(error);

      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    } finally {
      reset();
    }
  };

  return (
    <>
      <LogoBack path={AUTH_PATHS.LOGIN} />

      <Container>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack
            direction="column"
            gap={2}
            alignItems="center"
            sx={{ width: '100%' }}
          >
            <Typography variant="h2" sx={{ fontSize: 24 }}>
              {'Autenticazione a 2 fattori'}
            </Typography>

            <Stack gap={1} alignItems={'center'}>
              <Typography textAlign={'center'}>
                {
                  'Per garantirti un livello di sicurezza maggiore è necessario attivare, al primo accesso, l’autenticazione a due fattori. Consulta il video tutorial '
                }
                <span
                  style={{
                    fontWeight: 'bold',
                  }}
                >
                  <a
                    href="https://inbee.it/guida-autenticazione-multifattore/"
                    target="_blank"
                    style={{
                      //textDecoration: 'none',
                      color: '#000000',
                    }}
                    rel="noreferrer"
                  >
                    clicca qui
                  </a>
                </span>{' '}
                {' e segui i seguenti step:'}
              </Typography>

              <Typography textAlign={'center'}>
                {'1. '}
                <span style={{ color: '#DC143C', fontWeight: 'bold' }}>
                  {'IMPORTANTE'}
                </span>
                {
                  ' salvare il QR code e/o il codice qui sotto in un luogo sicuro per future configurazioni di altri cellulari.'
                }
              </Typography>
              <Typography mt={1} textAlign={'center'}>
                {
                  '2. Installa l’app di autenticazione sul cellulare. Suggeriamo'
                }{' '}
                <Link
                  href={
                    'https://www.microsoft.com/it-it/security/mobile-authenticator-app'
                  }
                  style={{ fontWeight: 'bold' }}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  Microsoft Authenticator
                </Link>
                {'.'}
              </Typography>
              <Typography mt={1} textAlign={'center'}>
                {
                  '3. Nell’app di autenticazione, inserisci il codice numerico o scansiona il QR code.'
                }
              </Typography>
            </Stack>

            {secretCode ? (
              <QRCodeCanvas value={qrCode} />
            ) : (
              <Skeleton variant="rectangular" width={128} height={128} />
            )}

            <CopyTextField
              fullWidth
              disabled
              value={secretCode}
              label={'Copia codice'}
              onClickCopyMessage={'Codice copiato correttamente!'}
            />

            <Stack gap={1} width={'100%'} alignItems={'center'}>
              <Typography textAlign={'center'}>
                {
                  '4. Inserisci qui sotto il codice generato dall’app di autenticazione.'
                }
              </Typography>
            </Stack>

            <OneTimeCodeTextField fieldsPrefix="code" />
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={!isValidForm}
            >
              {'Configura'}
            </LoadingButton>
          </Stack>
        </FormProvider>
      </Container>
    </>
  );
};

export default MfaSetup;
